import { ArrowDownward } from '@mui/icons-material';
import { Box, Button, Container, Typography } from '@mui/material';
import React from 'react';
import { FormattedMessage } from 'react-intl';

const Hero = ({ onScrollDown }) => {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          'linear-gradient(135deg, #000000 0%, #0a0a0a 25%, #1a1a1a 50%, #0f0f0f 75%, #000000 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle dark background elements */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          opacity: 0.3,
          backgroundImage: `
            radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.05) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.03) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.02) 0%, transparent 60%)
          `,
        }}
      />

      {/* Subtle grid pattern overlay */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          opacity: 0.3,
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            textAlign: 'center',
            color: 'white',
            py: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '80vh',
          }}
        >
          {/* AiCrafted Logo */}
          <Box
            component="img"
            src="https://aicrafted.ca/logo.png"
            alt="AiCrafted"
            sx={{
              maxWidth: { xs: '200px', sm: '280px', md: '350px' },
              width: '100%',
              height: 'auto',
              mb: 4,
              animation: 'fadeInUp 1s ease-out',
              '@keyframes fadeInUp': {
                '0%': { opacity: 0, transform: 'translateY(30px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          />

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap',
              animation: 'fadeInUp 1s ease-out 0.3s both',
              mb: { xs: 6, sm: 8 },
            }}
          >
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowDownward />}
              onClick={onScrollDown}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: '50px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.25)',
                  transform: 'translateY(-4px) scale(1.05)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
                },
              }}
            >
              <FormattedMessage id="hero.exploreButton" defaultMessage="Explore Projects" />
            </Button>
          </Box>

          {/* Stats Cards */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: { xs: 2, sm: 3, md: 4 },
              flexWrap: 'wrap',
              animation: 'fadeInUp 1s ease-out 0.6s both',
            }}
          >
            {[
              { number: '7', label: 'Innovative Projects', gradient: ['#9575cd', '#b39ddb'] },
              { number: '100%', label: 'Women-Led', gradient: ['#ba68c8', '#ce93d8'] },
              { number: '∞', label: 'Possibilities', gradient: ['#ec407a', '#f48fb1'] },
            ].map((stat, index) => (
              <Box
                key={stat.label}
                sx={{
                  textAlign: 'center',
                  px: { xs: 3, sm: 4 },
                  py: { xs: 3, sm: 4 },
                  borderRadius: 3,
                  bgcolor: `linear-gradient(135deg, ${stat.gradient[0]}40, ${stat.gradient[1]}30)`,
                  background: `linear-gradient(135deg, ${stat.gradient[0]}40, ${stat.gradient[1]}30)`,
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${stat.gradient[0]}50`,
                  minWidth: { xs: '140px', sm: '180px' },
                  flex: { xs: '1 1 auto', sm: '0 1 auto' },
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                    color: 'white',
                    textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                  }}
                >
                  {stat.number}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.95)',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    fontWeight: 500,
                    textShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
                  }}
                >
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>

      {/* Scroll indicator */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 40,
          left: '50%',
          transform: 'translateX(-50%)',
          animation: 'bounce 2s infinite',
          zIndex: 1,
          '@keyframes bounce': {
            '0%, 100%': { transform: 'translateX(-50%) translateY(0)' },
            '50%': { transform: 'translateX(-50%) translateY(-10px)' },
          },
        }}
      >
        <ArrowDownward sx={{ color: 'white', fontSize: 40, opacity: 0.8 }} />
      </Box>
    </Box>
  );
};

export default Hero;
